import React, { useState } from 'react';
import axios from 'axios';
import './ChangePassword.css';
import { useLanguage } from '../contexts/LanguageContext';
import SuccessModal from './shared/SuccessModal';
import { Button } from './shared';
import { validatePassword } from '../utils';

const ChangePassword = ({ onNavigateToLanding, onNavigateBack, onLogout, isOtpFlow = false }) => {
  const { t, language } = useLanguage(); // Issue #257: Get language for API request
  const [formData, setFormData] = useState({
    currentPassword: '', // Issue #18: User manually enters OTP in this field for OTP flow
    newPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = {
      ...formData,
      [name]: value
    };
    setFormData(updatedData);

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Issue #373: Real-time validation of "Confirm new password" so the user
    // sees the mismatch error (matching the registration flow) rather than a
    // silently-disabled submit button.
    if (name === 'newPassword' || name === 'confirmPassword') {
      if (updatedData.confirmPassword) {
        if (updatedData.newPassword !== updatedData.confirmPassword) {
          setErrors(prev => ({
            ...prev,
            confirmPassword: t('changePassword.errors.mismatch')
          }));
        } else {
          setErrors(prev => (
            prev.confirmPassword === t('changePassword.errors.mismatch')
              ? { ...prev, confirmPassword: '' }
              : prev
          ));
        }
      }
    }

    // Real-time strength check on the new password so the user sees the same
    // weak-password feedback as on the registration page instead of a silently
    // disabled submit button.
    if (name === 'newPassword') {
      if (updatedData.newPassword && !validatePassword(updatedData.newPassword)) {
        setErrors(prev => ({
          ...prev,
          newPassword: t('changePassword.errors.weak')
        }));
      } else {
        setErrors(prev => (
          prev.newPassword === t('changePassword.errors.weak')
            ? { ...prev, newPassword: '' }
            : prev
        ));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Issue #18: Only require current password if NOT OTP flow
    if (!isOtpFlow && !formData.currentPassword) {
      newErrors.currentPassword = t('changePassword.errors.currentRequired');
    }

    if (!formData.newPassword) {
      newErrors.newPassword = t('changePassword.errors.newRequired');
    } else if (!validatePassword(formData.newPassword)) {
      newErrors.newPassword = t('changePassword.errors.weak');
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('changePassword.errors.confirmRequired');
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('changePassword.errors.mismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('[ChangePassword] Form submitted, isOtpFlow:', isOtpFlow);

    if (!validateForm()) {
      console.log('[ChangePassword] Form validation failed');
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    setSuccessMessage('');

    try {
      console.log('[ChangePassword] Sending request to /api/change-password');
      const response = await axios.post('/api/change-password', {
        current_password: formData.currentPassword,
        new_password: formData.newPassword,
        confirm_password: formData.confirmPassword,
        language: language // Issue #257: Send language for email localization
      });

      console.log('[ChangePassword] Response received:', response.data);

      // Issue #133: Password change successful - show message and navigate
      if (response.data.success) {
        const successMsg = t('changePassword.successMessage') || 'Neues Passwort wurde erfolgreich festgelegt!';
        setSuccessMessage(successMsg);
        setShowSuccessModal(true);

        // Reset form
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        // Response is 200 but success is false
        setErrors({ general: response.data.message || t('changePassword.errors.serverError') });
      }
    } catch (error) {
      console.error('[ChangePassword] Error:', error);

      // Issue #133: Handle errors properly
      if (error.response) {
        if (error.response.status === 401) {
          // Session expired
          setErrors({ general: t('changePassword.errors.sessionExpired') || 'Sitzung abgelaufen. Bitte erneut anmelden.' });
          setTimeout(() => {
            if (onLogout) onLogout();
          }, 2000);
        } else if (error.response.data) {
          // Backend returned an error with message
          const { message, field } = error.response.data;
          if (field) {
            setErrors({ [field]: message });
          } else {
            setErrors({ general: message || t('changePassword.errors.serverError') });
          }
        } else {
          setErrors({ general: t('changePassword.errors.serverError') });
        }
      } else {
        // Network error or other issue
        setErrors({ general: 'Netzwerkfehler. Bitte überprüfe deine Internetverbindung.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    // Issue #18: Current password not required for OTP flow
    const currentPasswordValid = isOtpFlow || formData.currentPassword;

    return currentPasswordValid &&
           formData.newPassword &&
           formData.confirmPassword &&
           validatePassword(formData.newPassword) &&
           formData.newPassword === formData.confirmPassword;
  };

  // Issue #133: Handler for success modal close - navigate to landing page
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    if (onNavigateToLanding) {
      onNavigateToLanding();
    }
  };

  return (
    <div className="change-password-container">
      {/* Skip to main content link for keyboard users */}
      <a href="#main-content" className="skip-link">
        {t('a11y.skipToContent')}
      </a>

      {/* Navigation Bar */}
      <header className="cp-navigation-bar" role="banner">
        <h1 className="cp-page-title">{t('changePassword.title')}</h1>
      </header>

      {/* Main Content */}
      <main id="main-content" className="cp-main-content" role="main">
        <form onSubmit={handleSubmit} aria-label={t('changePassword.title')}>
          <div className="cp-form-header">
            <h2 className="cp-form-title">
              {isOtpFlow ? t('changePassword.otpFlowTitle') : t('changePassword.newPasswordTitle')}
            </h2>
            {isOtpFlow && (
              <p className="cp-form-subtitle" style={{ marginTop: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                {t('changePassword.otpFlowSubtitle')}
              </p>
            )}
          </div>

          {/* Current Password Field - Issue #133: Hidden in OTP flow since user doesn't know old password */}
          {!isOtpFlow && (
            <div className="cp-text-field-wrapper">
              <label htmlFor="cp-currentPassword" className="visually-hidden">
                {t('changePassword.currentPassword')}
              </label>
              <div className={`cp-text-field-input-wrapper ${errors.currentPassword ? 'error' : ''}`}>
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  id="cp-currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  placeholder={t('changePassword.currentPassword')}
                  className="cp-text-field-input-full"
                  aria-invalid={errors.currentPassword ? 'true' : 'false'}
                  aria-describedby={errors.currentPassword ? 'currentPassword-error' : undefined}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className={`cp-visibility-toggle ${showCurrentPassword ? 'active' : ''}`}
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  aria-label={showCurrentPassword ? t('a11y.hidePassword') : t('a11y.showPassword')}
                  aria-pressed={showCurrentPassword}
                >
                  <span aria-hidden="true">👁️</span>
                </button>
              </div>
              {errors.currentPassword && (
                <div id="currentPassword-error" className="cp-error-message" role="alert">
                  {errors.currentPassword}
                </div>
              )}
            </div>
          )}

          {/* New Password Field */}
          <div className="cp-text-field-wrapper">
            <label htmlFor="cp-newPassword" className="visually-hidden">
              {t('changePassword.newPassword')}
            </label>
            <div className={`cp-text-field-input-wrapper ${errors.newPassword ? 'error' : ''}`}>
              <input
                type={showNewPassword ? 'text' : 'password'}
                id="cp-newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder={t('changePassword.newPassword')}
                className="cp-text-field-input-full"
                aria-invalid={errors.newPassword ? 'true' : 'false'}
                aria-describedby={errors.newPassword ? 'newPassword-error' : undefined}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                className={`cp-visibility-toggle ${showNewPassword ? 'active' : ''}`}
                onClick={() => setShowNewPassword(!showNewPassword)}
                aria-label={showNewPassword ? t('a11y.hidePassword') : t('a11y.showPassword')}
                aria-pressed={showNewPassword}
              >
                <span aria-hidden="true">👁️</span>
              </button>
            </div>
            {errors.newPassword && (
              <div id="newPassword-error" className="cp-error-message" role="alert">
                {errors.newPassword}
              </div>
            )}
          </div>

          {/* Confirm New Password Field */}
          <div className="cp-text-field-wrapper">
            <label htmlFor="cp-confirmPassword" className="visually-hidden">
              {t('changePassword.confirmNewPassword')}
            </label>
            <div className={`cp-text-field-input-wrapper ${errors.confirmPassword ? 'error' : ''}`}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="cp-confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder={t('changePassword.confirmNewPassword')}
                className="cp-text-field-input-full"
                aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                className={`cp-visibility-toggle ${showConfirmPassword ? 'active' : ''}`}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? t('a11y.hidePassword') : t('a11y.showPassword')}
                aria-pressed={showConfirmPassword}
              >
                <span aria-hidden="true">👁️</span>
              </button>
            </div>
            {errors.confirmPassword && (
              <div id="confirmPassword-error" className="cp-error-message" role="alert">
                {errors.confirmPassword}
              </div>
            )}
          </div>

          {/* General Error */}
          {errors.general && (
            <div className="cp-error-message cp-general-error" role="alert" aria-live="polite">
              {errors.general}
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="cp-success-message" role="status" aria-live="polite">
              {successMessage}
            </div>
          )}

          {/* Submit Button */}
          <div className="cp-submit-button-wrapper">
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={!isFormValid() || isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? t('changePassword.submitting') : t('changePassword.submit')}
            </Button>
          </div>

          {/* Back Link - Issue #133: Hidden in OTP flow since user must set new password */}
          {!isOtpFlow && (onNavigateBack || onNavigateToLanding) && (
            <div className="cp-back-link-wrapper">
              <Button
                variant="link"
                onClick={onNavigateBack || onNavigateToLanding}
                aria-label={t('back')}
              >
                {t('back')}
              </Button>
            </div>
          )}
        </form>
      </main>

      {/* Issue #133: Success modal for password change confirmation */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        message={successMessage}
        autoCloseMs={3000}
      />
    </div>
  );
};

export default ChangePassword;
